import React, { Fragment, useCallback, useEffect } from 'react';
import { Container, Stack, Paper, Typography, Box, Chip } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import Back from '../../../components/common/Back.component';
import { useNavigate, useParams } from 'react-router-dom';
import Loading from '../../../components/common/Loading.component';
import DetailView from '../../../views/Detail.view';
import Button from '../../../components/common/Button.component';
import AddIcon from '@mui/icons-material/Add';
import TemplateDelete from '../../templates/Delete.template';
import technical from '../../../module/technical';
import _ from 'lodash';
import EditButton from '../../../components/detail/components/EditButton.component';
import LastUpdateInfo from '../../../components/lastUpdateInfo/LastUpdateInfo.component';
import DuplicateTemplate from '../../duplicate/Duplicate.template';

const AddButton = ({ to, label = 'ADD', id }) => {
  const navigate = useNavigate();

  const onClick = useCallback(
    (e) => {
      if (to) navigate(`${to}`, { state: { id: id } });
    },
    [navigate, to, id]
  );

  return (
    <Button variant='outlined' startIcon={<AddIcon />} onClick={onClick}>
      {label}
    </Button>
  );
};

const TechnicalDetailPage = ({ children, module }) => {
  const { tag, detail, query } = module;
  const { id } = useParams();
  const dispatch = useDispatch();
  const { jwt } = useSelector((state) => state.login);
  const { loading, data, error } = useSelector(detail.selector.data);
  const navigate = useNavigate();
  const userAuth = useSelector((state) => state.login?.user);

  useEffect(() => {
    if (userAuth.permissions[_.toUpper(tag)]?.READ === 'true') {
      dispatch(
        query.detail.action({
          settings: query.detail.settings,
          token: jwt,
          params: { id },
          query: { populate: query.detail.populate },
        })
      );
    }

    return () => {
      dispatch(
        query.detail.action({
          settings: null,
          token: null,
          params: null,
          query: null,
        })
      );
    };
    // eslint-disable-next-line
  }, [id, userAuth]);

  useEffect(() => {
    if (
      error &&
      (error?.response?.status === 404 || error?.response?.status === 500)
    )
      navigate('/404');
  }, [error, navigate]);

  return (
    <Container disableGutters={true} maxWidth={false} sx={{ height: '100%' }}>
      <Stack spacing={2} justifyContent='center' alignItems='center' width={1}>
        <Back title={tag} />
        {loading && <Loading />}
        <Paper variant='outlined' sx={{ padding: 2, width: '100%' }}>
          <Stack
            direction={'row-reverse'}
            justifyContent={'space-between'}
            spacing={1}
            mb={2}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent='flex-end'
              spacing={1}
            >
              {detail?.duplicate && (
                <DuplicateTemplate module={module} data={_.omit(data, id)} />
              )}
              {detail?.link &&
                userAuth.permissions['TECHNICAL_WORK']?.CREATE === 'true' && (
                  <AddButton
                    to={detail.link.to}
                    label={detail.link.label}
                    id={id}
                  />
                )}
              {detail?.edit &&
                userAuth.permissions[_.toUpper(tag)]?.UPDATE === 'true' && (
                  <EditButton to={detail.edit.to} tag={tag} />
                )}

              {userAuth.permissions[_.toUpper(tag)]?.DELETE === 'true' && (
                <TemplateDelete module={technical}>
                  {!_.isEmpty(data?.attributes?.id_technical_works?.data) && (
                    <Typography component='div' mt={1}>
                      Report number
                      <Box fontWeight={'bold'} display='inline'>
                        {` ${data?.attributes?.id_technical_works?.data.map(
                          (item) => ` ${item?.attributes?.report_number}`
                        )} `}
                      </Box>
                      will also be deleted.
                    </Typography>
                  )}
                </TemplateDelete>
              )}
            </Stack>
            {detail?.viewID && id && (
              <Box mb={2}>
                <Chip label={`ID: ${id}`} color='primary' variant='outlined' />
              </Box>
            )}
          </Stack>

          {userAuth.permissions[_.toUpper(tag)]?.READ === 'true' && (
            <Fragment>
              <DetailView
                data={data}
                selector={detail.selector.data}
                conf={detail.conf}
              />

              {children}
            </Fragment>
          )}
          {data?.attributes?.updatedAt && (
            <LastUpdateInfo
              updatedAt={data?.attributes?.updatedAt}
              username={
                data?.attributes?.id_user_last_updated?.data?.attributes
                  ?.id_users_permissions_user?.data?.attributes?.username
              }
            />
          )}
        </Paper>
      </Stack>
    </Container>
  );
};

export default TechnicalDetailPage;
